import { Basket } from '~/lib/data-contract/salesforce';
import { ClientSideSingleton as Session } from './session';
import { AltaPay } from './types';
import { finalizeOrder } from './lib/utils';
import ApplePay from './integrations/apple-pay';

type PaymentMethod = AltaPay.Schemas['PaymentMethod'];
type PaymentRequest =
    | {
          _type: AltaPayPaymentType.APPLEPAY;
          session: ApplePaySession;
      }
    | {
          _type: Exclude<AltaPayPaymentType, AltaPayPaymentType.APPLEPAY>;
          data: AltaPay.Schemas['Payment'];
      };

export enum AltaPayPaymentType {
    APPLEPAY = 'altapay_apple_pay',
    CREDIT_CARD = 'altapay_credit_card',
    MOBILEPAY = 'altapay_mobilepay',
    KLARNA = 'altapay_klarna',
}

class AltaPayPayment {
    type: AltaPayPaymentType | undefined;

    constructor(private _paymentMethod: PaymentMethod) {
        this.type = AltaPayPayment.getPaymentType(_paymentMethod);

        this.initiate = this.initiate.bind(this);
    }

    static getPaymentType(paymentMethod: PaymentMethod) {
        const { name, type, metadata, jsMethodHandler } = paymentMethod;

        // NOTE: This is unreliable as the payment method name may change at some point.
        switch (name) {
            case 'ApplePay':
                return AltaPayPaymentType.APPLEPAY;

            case 'Credit / Debit Card':
                return AltaPayPaymentType.CREDIT_CARD;

            case 'MobilePay':
                return AltaPayPaymentType.MOBILEPAY;

            case 'Klarna':
                return AltaPayPaymentType.KLARNA;

            // Provide fallback checks in case the payment method name changes.
            default:
                if (type === 'CARD') {
                    return AltaPayPaymentType.CREDIT_CARD;
                }

                if (metadata?.product === 'Klarna') {
                    return AltaPayPaymentType.KLARNA;
                }

                if (type === 'WALLET') {
                    if (jsMethodHandler?.identifier === 'ApplePay') {
                        return AltaPayPaymentType.APPLEPAY;
                    }
                }
        }
    }

    async initiate(orderNo: string, basket: Basket): Promise<PaymentRequest | undefined> {
        const sessionId = Session.getSessionId();

        if (!Session.token || !sessionId) {
            throw new Error('Payment cannot be initiated without a session.');
        }

        const id = this._paymentMethod.id;

        if (!this.type) throw new Error('Payment type is invalid.');

        switch (this._paymentMethod.onInitiatePayment.type) {
            case 'SCRIPT': {
                if (this.type === AltaPayPaymentType.APPLEPAY) {
                    const config = {
                        merchantIdentifier:
                            this._paymentMethod.jsMethodHandler?.data.merchantIdentifier ?? '',
                        authorizeUrl: this._paymentMethod.jsMethodHandler?.data.authorizeUrl ?? '',
                        sessionUrl: this._paymentMethod.jsMethodHandler?.data.sessionUrl ?? '',
                        operatingCountry:
                            this._paymentMethod.jsMethodHandler?.data.operatingCountry ?? '',
                    };

                    const ap = new ApplePay(id, orderNo, basket, config);

                    /**
                     * We will attempt to create the order during finalization of payment.
                     */
                    ap.setOrderVerification(() =>
                        finalizeOrder(orderNo, basket.basketId ?? '', basket.c_remainingTotal),
                    );

                    const session = ap.initiate();

                    return {
                        _type: AltaPayPaymentType.APPLEPAY,
                        session,
                    };
                }
                break;
            }

            case 'URL': {
                /**
                 * While this technically could never happen, we will explicitly imply
                 * it here for TypeScript to understand.
                 */
                if (this.type === AltaPayPaymentType.APPLEPAY) {
                    throw new Error('ApplePay can only be initiated via Javascript.');
                }

                const url = this._paymentMethod.onInitiatePayment.value;

                if (!url) throw new Error('Payment URL is missing.');

                const payload = {
                    sessionId,
                    paymentMethodId: this._paymentMethod.id,
                    /**
                     * NOTE: If "true", URLs will have URI schemas of to their respective apps.
                     */
                    mobileDeviceRequest: false,
                };

                // TODO(IMC-1523): Re-evaluate use of static /payment endpoint. We should be using 'url'.
                const data = await Session.authorizedRequest<
                    AltaPay.Schemas['Payment'],
                    AltaPay.Schemas['CreatePaymentRequest']
                >('/payment', {
                    method: 'POST',
                    body: payload,
                });

                return {
                    _type: this.type,
                    data,
                };
            }
        }

        return undefined;
    }

    async canRender(): Promise<boolean> {
        /**
         * At the moment, it's only ApplePay that requires pre-render checks.
         */
        if (this.type === AltaPayPaymentType.APPLEPAY) {
            const merchantId = this._paymentMethod.jsMethodHandler?.data.merchantIdentifier;

            return !!merchantId && (await ApplePay.canRender(merchantId));
        }

        return true;
    }
}

export default AltaPayPayment;
